import React from "react";
import { Link } from "react-router-dom";
import { Ticket, MapPin, MonitorPlay } from "lucide-react";
import { MainLayout } from "@/layouts/MainLayout";
import { useMockQuery } from "@/hooks/useMockQuery";
import { apiClient } from "@/lib/apiClient";
import { MyTicketCard } from "@/components/tickets/MyTicketCard";
import { EmptyState } from "@/components/states/EmptyState";
import { ErrorState } from "@/components/states/ErrorState";
import { Skeleton } from "@/components/ui/skeleton";

function Group({ title, icon: Icon, tickets }) {
  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <Icon className="h-5 w-5 text-brand" />
        <h2 className="font-heading text-xl font-extrabold tracking-tight text-foreground">
          {title}
        </h2>
        <span className="rounded-full bg-secondary px-2.5 py-0.5 text-sm font-semibold text-muted-foreground">
          {tickets.length}
        </span>
      </div>
      {tickets.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-secondary/40 px-6 py-10 text-center text-sm text-muted-foreground">
          No {title.toLowerCase()} yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          {tickets.map((t) => (
            <MyTicketCard key={`${t.event.id}-${t.ticket.type}`} item={t} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function MyTickets() {
  const { data, loading, error, isEmpty, refetch } = useMockQuery(
    () => apiClient.getMyTickets(),
    []
  );

  const inPerson = (data || []).filter((t) => t.ticket.type === "inPerson");
  const online = (data || []).filter((t) => t.ticket.type === "online");

  return (
    <MainLayout>
      <div className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="mb-8 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand/10 text-brand">
            <Ticket className="h-6 w-6" />
          </span>
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
              My Tickets
            </h1>
            <p className="text-muted-foreground">Your in-person passes and online access.</p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-44 w-full rounded-2xl" />
            ))}
          </div>
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : isEmpty ? (
          <EmptyState
            title="No tickets yet"
            description="Unlock a premium event to see your tickets here."
            actionLabel="Browse premium events"
            icon={Ticket}
          />
        ) : (
          <div className="space-y-12">
            <Group title="In-person tickets" icon={MapPin} tickets={inPerson} />
            <Group title="Online tickets" icon={MonitorPlay} tickets={online} />
          </div>
        )}
      </div>
    </MainLayout>
  );
}
